import { useEffect, useMemo } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { OnboardProvider, useOnboard } from "./OnboardContext"

const STEP_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]
const BASE_STEPS = ["contact", "number"]

function OnboardFrame() {
  const location = useLocation()
  const navigate = useNavigate()
  const { loading } = useOnboard()
  const pathname = typeof location?.pathname === "string" && location.pathname ? location.pathname : "/onboard/contact"
  const reducedMotion = useReducedMotion()
  const segment = useMemo(() => {
    const parts = pathname.split("/").filter(Boolean)
    return parts[1] ?? "contact"
  }, [pathname])

  const stepOrder = BASE_STEPS

  useEffect(() => {
    if (loading) {
      return
    }
    if (!stepOrder.includes(segment)) {
      const fallback = stepOrder[0] ?? "contact"
      navigate(`/onboard/${fallback}`)
    }
  }, [loading, navigate, segment, stepOrder])

  const index = Math.max(0, stepOrder.indexOf(segment))

  const goNext = () => {
    if (index >= stepOrder.length - 1) {
      navigate("/app", { replace: true })
    } else {
      const nextStep = stepOrder[index + 1]
      if (!nextStep) return
      navigate(`/onboard/${nextStep}`)
    }
  }

  const goPrev = () => {
    if (index <= 0) {
      navigate("/onboard/contact")
    } else {
      const previousStep = stepOrder[index - 1]
      if (!previousStep) return
      navigate(`/onboard/${previousStep}`)
    }
  }

  const motionProps = reducedMotion
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -12 },
        transition: { duration: 0.28, ease: STEP_EASE },
      }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={pathname} {...motionProps}>
        <Outlet
          context={{
            index,
            goNext,
            goPrev,
            step: index + 1,
            total: stepOrder.length,
            reducedMotion,
          }}
        />
      </motion.div>
    </AnimatePresence>
  )
}

export default function OnboardFlow() {
  return (
    <OnboardProvider>
      <OnboardFrame />
    </OnboardProvider>
  )
}
